import { defineStore } from 'pinia';
import consumer from '@/channels/consumer';
import NotificationService from '@/services/notificationService';

const DROPDOWN_LIMIT = 5;

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    subscription: null,
  }),

  getters: {
    hasUnread: (state) => state.unreadCount > 0,
  },

  actions: {
    async fetchNotifications() {
      this.isLoading = true;
      const response = await NotificationService.fetchNotifications({ rows: DROPDOWN_LIMIT });
      this.notifications = response.data.notifications;
      this.unreadCount = response.data.unread_count;
      this.isLoading = false;
    },

    addNotification(notification) {
      this.notifications.unshift(notification);
      if (this.notifications.length > DROPDOWN_LIMIT) {
        this.notifications.pop();
      }
      this.unreadCount++;
    },

    async markAsRead(id) {
      const notification = this.notifications.find(n => n.id === id);
      const wasUnread = Boolean(notification) && !notification.read_at;

      if (wasUnread) {
        notification.read_at = new Date().toISOString();
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }

      try {
        await NotificationService.markAsRead(id);
      } catch (error) {
        if (wasUnread) {
          notification.read_at = null;
          this.unreadCount++;
        }
        throw error;
      }
    },

    async markAllAsRead() {
      const previouslyUnread = this.notifications.filter(n => !n.read_at);
      const previousUnreadCount = this.unreadCount;

      previouslyUnread.forEach(n => { n.read_at = new Date().toISOString(); });
      this.unreadCount = 0;

      try {
        await NotificationService.markAllAsRead();
      } catch (error) {
        previouslyUnread.forEach(n => { n.read_at = null; });
        this.unreadCount = previousUnreadCount;
        throw error;
      }
    },

    subscribe() {
      if (this.subscription) return;

      this.subscription = consumer.subscriptions.create(
        { channel: "Noticed::NotificationChannel" },
        {
          received: (data) => {
            this.addNotification(data);
          },
        }
      );
    },

    unsubscribe() {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
    },

    reset() {
      this.unsubscribe();
      this.notifications = [];
      this.unreadCount = 0;
      this.isLoading = false;
    },
  },
});
